import moment from "moment";
import { X } from "@mynaui/icons-react";
import { StageDetails } from "../../../types/tournament";
import { IMatchDetailDTO } from "../../../models/IMatch";
import { formatDateLocal } from "../../../utils/dateFormat";
import { isAmericanoFormat, americanoFullCycleRounds } from "../../../utils/methods";
import { MatchConfig, GroupDateAssignment, VenueAssignment, GroupDefinition, VenueRange } from "../types";
import { Alert, Button, Card, DatePicker, Field, FormGrid, IconButton, Input, Select, StatCard, Tag } from "../../../ui";
import StepHeader from "./StepHeader";

interface Props {
  stage?: StageDetails;
  matches: IMatchDetailDTO[];
  groups: GroupDefinition[];
  bracketMatches: IMatchDetailDTO[];
  createWithEmptyTeams: boolean;
  selectedDates: Date[];
  matchConfigs: { [key: string]: MatchConfig };
  groupDateAssignments: { [groupId: string]: GroupDateAssignment };
  groupVenueAssignments: { [groupId: string]: VenueAssignment };
  knockoutVenueAssignments: { [roundName: string]: VenueAssignment };
  leagueVenueAssignments: VenueAssignment;
  venueRange: VenueRange;
  setVenueRange: React.Dispatch<React.SetStateAction<VenueRange>>;
  addDate: (date: Date) => void;
  removeDate: (dateStr: string) => void;
  updateConfig: (dateStr: string, field: "matchesPerDay" | "interval" | "startTime" | "venueCount" | "bestOf", value: number | Date) => void;
  addGroupDate: (groupId: string, date: Date) => void;
  removeGroupDate: (groupId: string, dateStr: string) => void;
  updateGroupVenueAssignment: (groupId: string, venueId: string, venueName: string, isAdding: boolean) => void;
  autoAssignVenuesToGroups: () => void;
  updateKnockoutVenueAssignment: (roundName: string, venueId: string, venueName: string, isAdding: boolean) => void;
  autoAssignVenuesToKnockoutRounds: () => void;
  updateLeagueVenueAssignment: (venueId: string, venueName: string, isAdding: boolean) => void;
  autoAssignVenuesToLeague: () => void;
  calculateTotalVenuesNeeded: () => number;
  nextHandler: (manualConfig: boolean) => void;
  americanoUnitCount?: number;
  americanoCourts?: number;
  americanoRounds?: number;
  updateAmericanoCourts?: (courts: number | undefined) => void;
  updateAmericanoRounds?: (rounds: number | undefined) => void;
}

const VENUE_NOTE = (
  <>
    <strong>Note:</strong> Venue assignments are optional. You can proceed without assigning
    venues and matches will be scheduled using standard logic. Venue assignments only affect how
    matches are distributed across venues when present.
  </>
);

const MatchFrequencyStep = ({
  stage, matches, groups, bracketMatches, createWithEmptyTeams,
  selectedDates, matchConfigs, groupDateAssignments,
  groupVenueAssignments, knockoutVenueAssignments, leagueVenueAssignments,
  venueRange, setVenueRange,
  addDate, removeDate, updateConfig,
  addGroupDate, removeGroupDate,
  updateGroupVenueAssignment, autoAssignVenuesToGroups,
  updateKnockoutVenueAssignment, autoAssignVenuesToKnockoutRounds,
  updateLeagueVenueAssignment, autoAssignVenuesToLeague,
  calculateTotalVenuesNeeded, nextHandler,
  americanoUnitCount = 0, americanoCourts, americanoRounds,
  updateAmericanoCourts, updateAmericanoRounds,
}: Props) => (
  <div className="cs-step match-frequency-container">
    <StepHeader
      title="Schedule Matches"
      subtitle="Configure when and how matches will be played. You can set general dates for all matches or assign specific dates to each group."
    />
    {createWithEmptyTeams && stage?.formatName === "Knockout" && (
      <Alert tone="info" className="empty-teams-info-banner">
        You're configuring timing for a knockout stage with empty teams. Teams will be automatically
        filled based on the final standings of precedent stages once they're completed.
      </Alert>
    )}
    <div className="body">
      {isAmericanoFormat(stage?.formatName) && (
        <Card
          className="americano-setup"
          title="Match Settings"
          subtitle="Set how the schedule is generated. Each round is played at the same time across the courts; you'll set the round times on the next step."
        >
          <FormGrid columns={3}>
            <Field label="Players">
              <p className="cs-static">{americanoUnitCount}</p>
            </Field>
            <Field label="Courts" hint="Matches at the same time each round">
              {(fieldId) => (
                <Input
                  id={fieldId}
                  type="number"
                  min={1}
                  placeholder="All"
                  value={americanoCourts ?? ""}
                  onChange={(e) =>
                    updateAmericanoCourts?.(
                      e.target.value.trim() === "" ? undefined : Math.max(1, parseInt(e.target.value, 10) || 0)
                    )
                  }
                />
              )}
            </Field>
            <Field
              label="Rounds"
              hint={<>Full cycle = {americanoFullCycleRounds(americanoUnitCount, americanoCourts)} rounds (no repeated partners)</>}
            >
              {(fieldId) => (
                <Input
                  id={fieldId}
                  type="number"
                  min={1}
                  value={americanoRounds ?? ""}
                  onChange={(e) =>
                    updateAmericanoRounds?.(
                      e.target.value.trim() === "" ? undefined : Math.max(1, parseInt(e.target.value, 10) || 0)
                    )
                  }
                />
              )}
            </Field>
          </FormGrid>
          <div className="setup-summary">
            {(() => {
              const full = Math.floor(americanoUnitCount / 4);
              const perRound = americanoCourts && americanoCourts >= 1 ? Math.min(full, americanoCourts) : full;
              const resting = americanoUnitCount - perRound * 4;
              return (
                <p>
                  {americanoRounds ?? 0} rounds · {perRound} match{perRound !== 1 ? "es" : ""} per round ·{" "}
                  {resting > 0 ? `${resting} resting each round` : "everyone plays every round"}
                </p>
              );
            })()}
          </div>
        </Card>
      )}
      {/* Tournament Overview */}
      {!isAmericanoFormat(stage?.formatName) && (
        <section className="cs-section tournament-overview">
          <h3 className="cs-section-title">Tournament Overview</h3>
          <div className="overview-stats">
            <StatCard label="Total Matches:" value={matches.length} />
            {stage?.formatName === "Group" && <StatCard label="Groups:" value={groups.length} tone="brand" />}
          </div>
        </section>
      )}

      {/* Scheduling Options */}
      {!isAmericanoFormat(stage?.formatName) && (
        <section className="cs-section scheduling-options">
          <h3 className="cs-section-title">Choose Your Scheduling Approach</h3>
          <div className="options-grid">
            <Card className="option-card" title="General Dates" subtitle="All matches use the same dates and settings">
              <div className="option-content">
                <Field label="Add Match Date">
                  {(fieldId) => (
                    <DatePicker
                      id={fieldId}
                      selected={null}
                      placeholderText="Click to select a date"
                      minDate={new Date(moment(stage?.startDate).format("YYYY-MM-DD"))}
                      maxDate={new Date(moment(stage?.endDate).format("YYYY-MM-DD"))}
                      onChange={(date: Date | null) => {
                        if (date !== null) addDate(date);
                      }}
                    />
                  )}
                </Field>
                {selectedDates.length > 0 && (
                  <div className="selected-dates">
                    <p className="cs-subhead">Selected Dates ({selectedDates.length})</p>
                    <div className="dates-list">
                      {selectedDates.map((date) => {
                        const dateStr = formatDateLocal(date);
                        const config = matchConfigs[dateStr];
                        return (
                          <div className="date-item" key={dateStr}>
                            <div className="date-info">
                              <span className="date-text">{moment(date).format("DD MMM YYYY")}</span>
                              <Tag tone="neutral" className="matches-count">{config?.matchesPerDay || 0} matches</Tag>
                            </div>
                            <IconButton
                              size="sm"
                              variant="danger"
                              className="remove-date-btn"
                              label="Remove date"
                              icon={<X size={16} />}
                              onClick={() => removeDate(dateStr)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {stage?.formatName === "Group" && groups.length > 0 && selectedDates.length > 1 && (
              <Card className="option-card" title="Group-Specific Dates" subtitle="Assign different configured dates to each group">
                <div className="option-content">
                  <p className="cs-subhead">Available Groups</p>
                  <div className="dates-list groups-list">
                    {groups.map((group) => {
                      const groupDates = groupDateAssignments[group.groupName]?.dates || [];
                      return (
                        <div key={group.groupName} className="date-item group-item">
                          <div className="date-info group-info">
                            <span className="date-text group-name">Group {group.groupName}</span>
                            <span className="group-teams">{group.teamIds.length} teams</span>
                          </div>
                          {groupDates.length > 0 ? (
                            <Tag tone="ok" className="dates-count">{groupDates.length} dates assigned</Tag>
                          ) : (
                            <Tag tone="neutral" className="no-dates">No dates assigned</Tag>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Card>
            )}
          </div>
        </section>
      )}

      {/* Quick Actions */}
      {!isAmericanoFormat(stage?.formatName) && (
        <Card className="quick-actions-section" title="Quick Actions" subtitle="Need more control over individual matches?">
          <Button variant="tint" className="action-btn secondary" onClick={() => nextHandler(true)}>
            Skip to Manual Configuration
          </Button>
        </Card>
      )}

      {/* Detailed Configuration Sections */}
      {!isAmericanoFormat(stage?.formatName) && selectedDates.length > 0 && (
        <section className="cs-section detailed-configuration">
          <h3 className="cs-section-title">Configure Match Settings</h3>
          <div className="config-cards">
            {selectedDates.map((date) => {
              const dateStr = formatDateLocal(date);
              const config = matchConfigs[dateStr];
              return (
                <Card
                  className="config-card"
                  key={dateStr}
                  title={moment(date).format("DD MMMM YYYY")}
                  actions={
                    <IconButton
                      size="sm"
                      variant="danger"
                      className="remove-btn"
                      label="Remove date"
                      icon={<X size={16} />}
                      onClick={() => removeDate(dateStr)}
                    />
                  }
                >
                  <div className="config-fields">
                    <Field label="Start Time">
                      {(fieldId) => (
                        <DatePicker
                          id={fieldId}
                          mode="time"
                          selected={config.startTime}
                          onChange={(date: Date | null) => {
                            if (date) {
                              const [year, month, day] = dateStr.split("-").map(Number);
                              const newStartTime = new Date(year, month - 1, day, date.getHours(), date.getMinutes(), 0, 0);
                              updateConfig(dateStr, "startTime", newStartTime);
                            }
                          }}
                          timeIntervals={15}
                          timeCaption="Start Time"
                          dateFormat="hh:mm a"
                        />
                      )}
                    </Field>
                    <Field label="Matches per day">
                      {(fieldId) => (
                        <Input id={fieldId} type="number" min="1" value={config.matchesPerDay || ""} onChange={(e) => updateConfig(dateStr, "matchesPerDay", Number(e.target.value))} placeholder="e.g., 3" />
                      )}
                    </Field>
                    <Field label="Interval (minutes)">
                      {(fieldId) => (
                        <Input id={fieldId} type="number" min="5" step="5" value={config.interval || ""} onChange={(e) => updateConfig(dateStr, "interval", Number(e.target.value))} placeholder="e.g., 30" />
                      )}
                    </Field>
                    <Field label="Venues">
                      {(fieldId) => (
                        <Input id={fieldId} type="number" min="1" value={config.venueCount || ""} onChange={(e) => updateConfig(dateStr, "venueCount", Number(e.target.value))} placeholder="e.g., 2" />
                      )}
                    </Field>
                    <Field label="Best of">
                      {(fieldId) => (
                        <Input id={fieldId} type="number" min="1" value={config.bestOf || ""} onChange={(e) => updateConfig(dateStr, "bestOf", Number(e.target.value))} placeholder="e.g., 3" />
                      )}
                    </Field>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* Group-Specific Configuration */}
      {stage?.formatName === "Group" && groups.length > 0 && selectedDates.length > 1 && (
        <section className="cs-section group-specific-config">
          <h3 className="cs-section-title">Configure Group-Specific Dates</h3>
          <p className="section-description">
            Assign specific dates to each group from your configured dates. Each group can be assigned to
            play on different dates with their own match settings.
          </p>
          <div className="groups-config-grid">
            {groups.map((group) => {
              const groupDates = groupDateAssignments[group.groupName]?.dates || [];
              const groupConfigs = groupDateAssignments[group.groupName]?.configs || {};
              return (
                <Card
                  className="group-config-card"
                  key={group.groupName}
                  title={`Group ${group.groupName}`}
                  actions={
                    <>
                      <Tag tone="brand" className="team-count">{group.teamIds.length} teams</Tag>
                      <Tag tone="neutral" className="date-count">{groupDates.length} dates</Tag>
                    </>
                  }
                >
                  <div className="group-content">
                    <Field label={`Assign Date to Group ${group.groupName}`}>
                      {(fieldId) => (
                        <Select
                          id={fieldId}
                          block
                          className="date-select"
                          value=""
                          onChange={(e) => {
                            if (e.target.value) {
                              const selectedDate = selectedDates.find((date) => formatDateLocal(date) === e.target.value);
                              if (selectedDate) addGroupDate(group.groupName, selectedDate);
                            }
                          }}
                        >
                          <option value="">Select a configured date</option>
                          {selectedDates
                            .filter((date) => {
                              const dateStr = formatDateLocal(date);
                              return !groupDateAssignments[group.groupName]?.dates.some((assignedDate) => formatDateLocal(assignedDate) === dateStr);
                            })
                            .map((date) => {
                              const dateStr = formatDateLocal(date);
                              const config = matchConfigs[dateStr];
                              return (
                                <option key={dateStr} value={dateStr}>
                                  {moment(date).format("DD MMM YYYY")} ({config?.matchesPerDay || 0} matches)
                                </option>
                              );
                            })}
                        </Select>
                      )}
                    </Field>
                    {groupDates.length > 0 && (
                      <div className="group-dates-list">
                        <p className="cs-subhead">Assigned Dates ({groupDates.length})</p>
                        <div className="dates-list">
                          {groupDates.map((date) => {
                            const dateStr = formatDateLocal(date);
                            const config = groupConfigs[dateStr];
                            return (
                              <div className="date-item" key={dateStr}>
                                <div className="date-info">
                                  <span className="date-text">{moment(date).format("DD MMM YYYY")}</span>
                                  <Tag tone="neutral" className="matches-count">{config?.matchesPerDay || 0} matches</Tag>
                                </div>
                                <IconButton
                                  size="sm"
                                  variant="danger"
                                  className="remove-date-btn"
                                  label="Remove date"
                                  icon={<X size={14} />}
                                  onClick={() => removeGroupDate(group.groupName, dateStr)}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* Group-Venue Assignment */}
      {stage?.formatName === "Group" && groups.length > 0 && selectedDates.length > 0 && (
        <Card
          className="venue-assignment group-venue-assignment"
          title="Assign Groups to Venues (Optional)"
          subtitle="Each group can be assigned to a specific venue where all their matches will be played consecutively. This is optional - if no venues are assigned, matches will be scheduled using standard logic."
          actions={
            <Button variant="tint" size="sm" className="auto-assign-btn" onClick={autoAssignVenuesToGroups}>
              Auto-assign Venues
            </Button>
          }
        >
          <VenueRangeConfig venueRange={venueRange} setVenueRange={setVenueRange} totalVenuesNeeded={calculateTotalVenuesNeeded()} />
          <div className="venue-grid group-venue-grid">
            {groups.map((group) => (
              <Card
                key={group.groupName}
                padding="sm"
                className="group-venue-card"
                title={`Group ${group.groupName}`}
                actions={
                  <>
                    <Tag tone="brand" className="team-count">{group.teamIds.length} teams</Tag>
                    <Tag tone="neutral" className="match-count">{(group.teamIds.length * (group.teamIds.length - 1)) / 2} matches</Tag>
                  </>
                }
              >
                <div className="group-teams-preview">
                  {group.teamIds.length > 0 ? (
                    group.teamIds.slice(0, 2).map((teamId) => {
                      const team = stage?.teams.find((t) => t.id === teamId);
                      return <span key={teamId} className="team-preview">{team?.teamName}</span>;
                    })
                  ) : (
                    <span className="empty-group">No teams assigned</span>
                  )}
                  {group.teamIds.length > 2 && <span className="more-teams">+{group.teamIds.length - 2} more</span>}
                </div>
                <VenueButtons
                  venueRange={venueRange}
                  assignedVenueIds={groupVenueAssignments[group.groupName]?.venueIds || []}
                  onToggle={(venueId, venueName, isAssigned) =>
                    updateGroupVenueAssignment(group.groupName, venueId, venueName, !isAssigned)
                  }
                />
                {groupVenueAssignments[group.groupName]?.venueIds?.length > 0 && (
                  <div className="venue-info">
                    <div className="venue-assignment-summary">
                      <p><strong>Assigned Venues:</strong> {groupVenueAssignments[group.groupName]?.venueNames?.join(", ") || "None"}</p>
                      <p><strong>Venues per Group:</strong> {groupVenueAssignments[group.groupName]?.venueIds?.length || 0} venue(s)</p>
                    </div>
                    <p className="scheduling-note">Matches will be scheduled consecutively across assigned venues</p>
                  </div>
                )}
              </Card>
            ))}
          </div>
          <Alert tone="info" className="optional-note">{VENUE_NOTE}</Alert>
        </Card>
      )}

      {/* Knockout Venue Assignment */}
      {stage?.formatName === "Knockout" && bracketMatches.length > 0 && selectedDates.length > 0 && (
        <Card
          className="venue-assignment knockout-venue-assignment"
          title="Assign Rounds to Venues (Optional)"
          subtitle="Each knockout round can be assigned to specific venues where all matches in that round will be played. This is optional - if no venues are assigned, matches will be scheduled using standard logic."
          actions={
            <Button variant="tint" size="sm" className="auto-assign-btn" onClick={autoAssignVenuesToKnockoutRounds}>
              Auto-assign Venues
            </Button>
          }
        >
          <VenueRangeConfig venueRange={venueRange} setVenueRange={setVenueRange} totalVenuesNeeded={calculateTotalVenuesNeeded()} />
          <div className="venue-grid round-venue-grid">
            {Array.from(new Set(bracketMatches.map((x) => x.round))).map((roundName) => {
              const roundMatches = bracketMatches.filter((x) => x.round === roundName);
              return (
                <Card
                  key={roundName}
                  padding="sm"
                  className="round-venue-card"
                  title={roundName}
                  actions={<Tag tone="neutral" className="match-count">{roundMatches.length} matches</Tag>}
                >
                  <div className="group-teams-preview round-matches-preview">
                    {roundMatches.length > 0 ? (
                      roundMatches.slice(0, 2).map((match) => (
                        <span key={match.id} className="team-preview match-preview">
                          {match.homeTeamName || "TBD"} vs {match.awayTeamName || "TBD"}
                        </span>
                      ))
                    ) : (
                      <span className="empty-group empty-round">No matches</span>
                    )}
                    {roundMatches.length > 2 && <span className="more-teams more-matches">+{roundMatches.length - 2} more</span>}
                  </div>
                  <VenueButtons
                    venueRange={venueRange}
                    assignedVenueIds={knockoutVenueAssignments[roundName]?.venueIds || []}
                    onToggle={(venueId, venueName, isAssigned) =>
                      updateKnockoutVenueAssignment(roundName, venueId, venueName, !isAssigned)
                    }
                  />
                  {knockoutVenueAssignments[roundName]?.venueIds?.length > 0 && (
                    <div className="venue-info">
                      <div className="venue-assignment-summary">
                        <p><strong>Assigned Venues:</strong> {knockoutVenueAssignments[roundName]?.venueNames?.join(", ") || "None"}</p>
                        <p><strong>Venues per Round:</strong> {knockoutVenueAssignments[roundName]?.venueIds?.length || 0} venue(s)</p>
                      </div>
                      <p className="scheduling-note">Matches will be scheduled across assigned venues for this round</p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
          <Alert tone="info" className="optional-note">{VENUE_NOTE}</Alert>
        </Card>
      )}

      {/* League Venue Assignment */}
      {stage?.formatName === "League" && matches.length > 0 && selectedDates.length > 0 && (
        <Card
          className="venue-assignment league-venue-assignment"
          title="Assign Venues to League (Optional)"
          subtitle="Assign specific venues where league matches will be played. You can assign multiple venues and matches will be distributed across them. This is optional - if no venues are assigned, matches will be scheduled using standard logic."
          actions={
            <Button variant="tint" size="sm" className="auto-assign-btn" onClick={autoAssignVenuesToLeague}>
              Auto-assign Venues
            </Button>
          }
        >
          <VenueRangeConfig venueRange={venueRange} setVenueRange={setVenueRange} totalVenuesNeeded={calculateTotalVenuesNeeded()} />
          <div className="league-venue-selection">
            <VenueButtons
              venueRange={venueRange}
              assignedVenueIds={leagueVenueAssignments.venueIds || []}
              onToggle={(venueId, venueName, isAssigned) =>
                updateLeagueVenueAssignment(venueId, venueName, !isAssigned)
              }
            />
          </div>
          {leagueVenueAssignments.venueIds?.length > 0 && (
            <div className="venue-info">
              <div className="venue-assignment-summary">
                <p><strong>Assigned Venues:</strong> {leagueVenueAssignments.venueNames?.join(", ") || "None"}</p>
                <p><strong>Total Venues:</strong> {leagueVenueAssignments.venueIds?.length || 0} venue(s)</p>
              </div>
              <p className="scheduling-note">
                Matches will be scheduled across assigned venues, distributing evenly based on available time slots
              </p>
            </div>
          )}
          <Alert tone="info" className="optional-note">{VENUE_NOTE}</Alert>
        </Card>
      )}
    </div>
  </div>
);

/* Shared sub-components to reduce duplication */

const VenueRangeConfig = ({
  venueRange, setVenueRange, totalVenuesNeeded,
}: {
  venueRange: VenueRange;
  setVenueRange: React.Dispatch<React.SetStateAction<VenueRange>>;
  totalVenuesNeeded: number;
}) => (
  <div className="venue-range-config">
    <span className="cs-label">Venue Range:</span>
    <div className="venue-range-info">
      <span className="auto-label">Auto-calculated from match settings</span>
      <Tag tone="brand" className="venue-count">
        {totalVenuesNeeded} venue{totalVenuesNeeded !== 1 ? "s" : ""} needed
      </Tag>
    </div>
    <div className="venue-range-inputs">
      <Input
        inputSize="sm"
        block={false}
        type="number"
        min="1"
        aria-label="Start"
        value={venueRange.start || ""}
        onChange={(e) => setVenueRange((prev) => ({ ...prev, start: parseInt(e.target.value) || 0 }))}
        onBlur={(e) => { if (!parseInt(e.target.value)) setVenueRange((prev) => ({ ...prev, start: 1 })); }}
        placeholder="Start"
      />
      <span>to</span>
      <Input
        inputSize="sm"
        block={false}
        type="number"
        min={venueRange.start || 1}
        aria-label="End"
        value={venueRange.end || ""}
        onChange={(e) => setVenueRange((prev) => ({ ...prev, end: parseInt(e.target.value) || 0 }))}
        onBlur={(e) => { if (!parseInt(e.target.value)) setVenueRange((prev) => ({ ...prev, end: prev.start || 1 })); }}
        placeholder="End"
      />
    </div>
  </div>
);

const VenueButtons = ({
  venueRange, assignedVenueIds, onToggle,
}: {
  venueRange: VenueRange;
  assignedVenueIds: string[];
  onToggle: (venueId: string, venueName: string, isAssigned: boolean) => void;
}) => (
  <div className="venue-selection">
    <span className="cs-label">Assign Venues:</span>
    <div className="venue-options">
      {Array.from({ length: venueRange.end - venueRange.start + 1 }, (_, i) => {
        const venueNumber = venueRange.start + i;
        const venueId = `venue-${venueNumber}`;
        const venueName = `Court ${venueNumber}`;
        const isAssigned = assignedVenueIds.includes(venueId);
        return (
          <Button
            key={venueNumber}
            size="sm"
            variant={isAssigned ? "primary" : "ghost"}
            className={`venue-option ${isAssigned ? "assigned" : ""}`}
            aria-pressed={isAssigned}
            iconRight={isAssigned ? <span className="checkmark">✓</span> : undefined}
            onClick={() => onToggle(venueId, venueName, isAssigned)}
          >
            {venueName}
          </Button>
        );
      })}
    </div>
  </div>
);

export default MatchFrequencyStep;
